
import { NotificationTemplate, NotificationPreference } from './types';

// Mock data for notification preferences
let notificationPreferences: NotificationPreference[] = [];

// Mock data for notification templates
let notificationTemplates: NotificationTemplate[] = [
  {
    id: 'template-1',
    name: 'Welcome Email',
    type: 'email',
    subject: 'Welcome to Our Referral Program!',
    body: 'Hello {name}, welcome to our referral program. Start referring friends today!',
    content: 'Hello {name}, welcome to our referral program. Start referring friends today!',
    template: 'Hello {name}, welcome to our referral program. Start referring friends today!',
    variables: ['name'],
    isDefault: true,
    createdAt: new Date('2023-01-01').toISOString(),
    updatedAt: new Date('2023-01-01').toISOString()
  },
  {
    id: 'template-2',
    name: 'Referral Success',
    type: 'email',
    subject: 'Your Referral Was Successful!',
    body: 'Congratulations {name}, your referral was successful. You\'ve earned {points} points!',
    content: 'Congratulations {name}, your referral was successful. You\'ve earned {points} points!',
    template: 'Congratulations {name}, your referral was successful. You\'ve earned {points} points!',
    variables: ['name', 'points'],
    isDefault: true,
    createdAt: new Date('2023-01-05').toISOString(),
    updatedAt: new Date('2023-01-05').toISOString()
  }
];

// Send an email notification
export const sendEmailNotification = async (
  toEmail: string,
  templateId: string,
  variables: Record<string, any>
): Promise<boolean> => {
  const template = notificationTemplates.find(t => t.id === templateId);
  
  if (!template) {
    console.error(`Template not found: ${templateId}`);
    return false;
  }
  
  // In a real app, this would use an email service provider
  console.log(`Sending email to ${toEmail}`);
  console.log(`Subject: ${template.subject}`);
  
  // Replace variables in the template
  let body = template.body;
  for (const [key, value] of Object.entries(variables)) {
    body = body.replace(`{${key}}`, value);
  }
  
  console.log(`Body: ${body}`);
  
  // Simulate successful sending
  return true;
};

// Send a referral notification
export const sendReferralNotification = async (
  referralId: string,
  type: 'new' | 'approved' | 'rejected'
): Promise<boolean> => {
  // In a real app, this would get the referral from database
  // and send appropriate notification
  console.log(`Sending ${type} referral notification for referral ${referralId}`);
  
  // Simulate successful sending
  return true;
};

// Add default email templates
export const addDefaultEmailTemplates = (): NotificationTemplate[] => {
  const defaultTemplates: NotificationTemplate[] = [
    {
      id: 'welcome-template',
      name: 'Welcome Email',
      type: 'email',
      subject: 'Welcome to Our Referral Program',
      body: 'Hello {name}, welcome to our referral program!',
      content: 'Hello {name}, welcome to our referral program!',
      template: 'Hello {name}, welcome to our referral program!',
      isDefault: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      variables: []
    },
    {
      id: 'referral-approved',
      name: 'Referral Approved',
      type: 'email',
      subject: 'Your Referral Was Approved',
      body: 'Your referral for {refereeName} was approved. You\'ve earned {points} points!',
      content: 'Your referral for {refereeName} was approved. You\'ve earned {points} points!',
      template: 'Your referral for {refereeName} was approved. You\'ve earned {points} points!',
      isDefault: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      variables: ['refereeName', 'points']
    }
  ];
  
  notificationTemplates = [...notificationTemplates, ...defaultTemplates];
  return defaultTemplates;
};

// Notify a user about a referral status change
export const notifyReferralStatus = async (
  referralId: string,
  status: string
): Promise<boolean> => {
  // In a real app, this would fetch the referral and send a notification
  console.log(`Notifying about referral ${referralId} status change to ${status}`);
  
  // Simulate successful notification
  return true;
};

// Get user notification preference
export const getUserNotificationPreference = async (
  userId: string
): Promise<NotificationPreference | null> => {
  const preference = notificationPreferences.find(p => p.userId === userId);
  return preference || null;
};

// Get all notification templates - ADD THIS MISSING FUNCTION
export const getNotificationTemplates = async (): Promise<NotificationTemplate[]> => {
  return [...notificationTemplates];
};

// Set user notification preference
export const setUserNotificationPreference = async (
  userId: string,
  preferences: Omit<NotificationPreference, 'userId' | 'updatedAt' | 'id'>
): Promise<NotificationPreference> => {
  const existingIndex = notificationPreferences.findIndex(p => p.userId === userId);
  const updatedPreference: NotificationPreference = {
    id: existingIndex >= 0 ? notificationPreferences[existingIndex].id : `pref-${Date.now()}`,
    userId,
    ...preferences,
    updatedAt: new Date().toISOString()
  };
  
  if (existingIndex >= 0) {
    notificationPreferences[existingIndex] = updatedPreference;
  } else {
    notificationPreferences.push(updatedPreference);
  }
  
  return updatedPreference;
};

// Get all notification preferences
export const getNotificationPreferences = async (): Promise<NotificationPreference[]> => {
  return [...notificationPreferences];
};

// Send referral activation email
export const sendReferralActivationEmail = async (
  email: string,
  name: string,
  activationLink: string
): Promise<boolean> => {
  // In a real app, this would use an email service provider
  console.log(`Sending activation email to ${email}`);
  console.log(`Subject: Activate your referral account`);
  console.log(`Body: Hello ${name}, please activate your account using this link: ${activationLink}`);
  
  // Simulate successful sending
  return true;
};
